// src/services/authInterceptor.ts
import { config } from "../../config/env";
import { LogoutManager } from "../../utils/Authentication/logoutUtils";
import { secureStorage } from "../Storage/secureStorage";
import { handleTokenError } from "../../utils/Authentication/authErrorHandler";
import { tokenMonitor } from "./tokenMonitor";
import { logger } from "../../utils/logger";
import { supabase } from "../../lib/supabase";

// Type definitions
interface RequestConfig {
  url?: string;
  method?: string;
  headers?: any;
  body?: any;
  mode?: string;
  credentials?: string;
  cache?: string;
  redirect?: string;
  referrer?: string;
  integrity?: string;
}

class AuthInterceptor {
  private refreshPromise: Promise<any> | null = null;

  async interceptRequest(requestConfig: any) {
    const token = this.getAccessToken();
    if (token) {
      requestConfig.headers = {
        ...requestConfig.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return requestConfig;
  }

  async interceptResponse(response: Response, originalRequest?: RequestConfig): Promise<Response> {
    if (response.status === 401) {
      return this.handleTokenExpired(response, originalRequest);
    }
    
    // Handle rate limiting errors by throwing an error
    if (response.status === 429) {
      await this.handleRateLimitError(response);
      // This line will never be reached because handleRateLimitError throws
      return response;
    }
    
    return response;
  }

  private async handleRateLimitError(response: Response) {
    try {
      const data = await response.json();
      
      // Create a structured error response with rate limit information
      const rateLimitError = new Error('Rate limit exceeded');
      (rateLimitError as any).response = {
        status: 429,
        data: data,
        headers: {
          'retry-after': response.headers.get('retry-after'),
          'x-rate-limit-limit': response.headers.get('x-rate-limit-limit'),
          'x-rate-limit-remaining': response.headers.get('x-rate-limit-remaining'),
          'x-rate-limit-reset': response.headers.get('x-rate-limit-reset'),
          'x-rate-limit-violations': response.headers.get('x-rate-limit-violations'),
          'x-rate-limit-delay-level': response.headers.get('x-rate-limit-delay-level'),
        }
      };
      
      // Return the error as a rejected promise to be handled by the calling code
      throw rateLimitError;
    } catch (_error) {
      // If we can't parse the response, still throw a rate limit error
      const fallbackError = new Error('Rate limit exceeded');
      (fallbackError as any).response = {
        status: 429,
        data: { error: 'RATE_LIMITED', message: 'Too many requests' },
        headers: { 'retry-after': '60' }
      };
      throw fallbackError;
    }
  }

  private async handleTokenExpired(originalResponse: Response, originalRequest?: RequestConfig) {
    try {
      const newTokens = await this.refreshTokens();
      if (newTokens && originalRequest?.url) {
        // Retry original request with new token
        return this.retryRequest(originalRequest, newTokens.access_token);
      }
    } catch (error) {
      console.error("Token refresh failed, redirecting to login:", error);
      
      // Use enhanced error handling
      const authError = handleTokenError(error, "token_expired_handling");
      
      // Check if we should redirect to login
      if (authError.code === 'TOKEN_EXPIRED' || authError.code === 'TOKEN_INVALID' || authError.code === 'REFRESH_FAILED' || authError.code === 'UNAUTHORIZED') {
        this.redirectToLogin();
      }
    }
    return originalResponse;
  }

  private async refreshTokens() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  /**
   * Parse JWT token to extract payload and check expiration
   * @param token JWT token string
   * @returns Parsed token payload or null if invalid
   */
  private parseJWTToken(token: string): any {
    try {
      if (!token || typeof token !== 'string') {
        return null;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      
      const base64Url = parts[1];
      if (!base64Url) {
        return null;
      }
      
      // Convert base64url to base64
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      // Decode and parse JSON payload
      const jsonPayload = decodeURIComponent(
        atob(padded)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      
      return JSON.parse(jsonPayload);
    } catch (error) {
      // Invalid token format
      return null;
    }
  }

  /**
   * Get a usable session, refreshing if needed.
   *
   * @param force Skip the "the token has not expired yet, so it must be fine"
   *   shortcut and mint a new token from the refresh token. Pass this when the
   *   SERVER rejected the token: `exp` in the future only means the token has
   *   not timed out, not that Supabase still honours it. A revoked session, a
   *   deleted user or a rotated JWT secret all produce a token that is
   *   unexpired and rejected, and without force the caller gets the same dead
   *   token handed back forever.
   */
  async performRefresh(force: boolean = false) {
    // Mock credentials mode - skip Supabase refresh, just return stored tokens
    if (config.dev.mockCredentials) {
      const accessToken = this.getAccessToken();
      const refreshToken = secureStorage.getRefreshToken();
      const user = secureStorage.getUserData();
      
      if (accessToken && refreshToken && user) {
        console.log('🔓 Mock credentials mode: Using stored mock tokens');
        return {
          access_token: accessToken,
          refresh_token: refreshToken,
          user: user
        };
      }
      return null;
    }
    
    // First, check if Supabase has a valid session (source of truth)
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (session?.refresh_token && session?.access_token) {
        // Check if access token is expired
        const tokenPayload = this.parseJWTToken(session.access_token);
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (tokenPayload?.exp && tokenPayload.exp > currentTime && !force) {
          // Supabase has a valid, non-expired session - use it directly
          if (process.env.NODE_ENV === 'development') {
            logger.log("Using Supabase session (valid, non-expired)");
          }
          this.saveTokens(session.access_token, session.refresh_token);
          return {
            access_token: session.access_token,
            refresh_token: session.refresh_token,
            user: session.user
          };
        } else if (session.refresh_token) {
          // Either the access token expired, or the server rejected it and the
          // caller forced a refresh. Both are answered the same way.
          if (process.env.NODE_ENV === 'development') {
            logger.log(force
              ? "Token rejected by server, forcing a refresh via Supabase"
              : "Access token expired, refreshing via Supabase");
          }
          
          try {
            const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession({
              refresh_token: session.refresh_token
            });
            
            if (refreshedSession?.session && !refreshError) {
              // Supabase refresh successful
              if (process.env.NODE_ENV === 'development') {
                logger.log("Supabase refresh successful");
              }
              this.saveTokens(
                refreshedSession.session.access_token,
                refreshedSession.session.refresh_token
              );
              return refreshedSession.session;
            } else {
              // Supabase refresh failed - tokens are invalid
              if (process.env.NODE_ENV === 'development') {
                logger.log("Supabase refresh failed - clearing tokens");
              }
              this.clearTokens();
              return null;
            }
          } catch (supabaseRefreshError) {
            // Supabase refresh threw an error - tokens are invalid
            if (process.env.NODE_ENV === 'development') {
              logger.log("Supabase refresh error - clearing tokens:", supabaseRefreshError);
            }
            this.clearTokens();
            return null;
          }
        }
      }
      
      // If we reach here, Supabase has no session
      // Try to restore session from stored tokens
      const storedRefreshToken = secureStorage.getRefreshToken();
      const storedAccessToken = secureStorage.getAccessToken();
      const storedUser = secureStorage.getUserData();
      
      if (storedRefreshToken && storedAccessToken && storedUser) {
        // Try to set the session in Supabase using stored tokens
        try {
          const { data: { session }, error: setSessionError } = await supabase.auth.setSession({
            access_token: storedAccessToken,
            refresh_token: storedRefreshToken
          });
          
          if (session && !setSessionError) {
            // Successfully restored session in Supabase
            if (process.env.NODE_ENV === 'development') {
              logger.log("Restored Supabase session from stored tokens");
            }
            return {
              access_token: session.access_token,
              refresh_token: session.refresh_token,
              user: session.user
            };
          } else {
            // Try refreshing with stored refresh token
            try {
              const { data: refreshedSession, error: refreshError } = await supabase.auth.refreshSession({
                refresh_token: storedRefreshToken
              });
              
              if (refreshedSession?.session && !refreshError) {
                // Successfully refreshed session
                if (process.env.NODE_ENV === 'development') {
                  logger.log("Refreshed Supabase session from stored refresh token");
                }
                this.saveTokens(
                  refreshedSession.session.access_token,
                  refreshedSession.session.refresh_token
                );
                return refreshedSession.session;
              }
            } catch (refreshErr) {
              // Refresh failed - tokens are invalid
              if (process.env.NODE_ENV === 'development') {
                logger.log("Failed to refresh session from stored tokens:", refreshErr);
              }
            }
          }
        } catch (setSessionErr) {
          // Failed to set session - tokens might be invalid
          if (process.env.NODE_ENV === 'development') {
            logger.log("Failed to set Supabase session from stored tokens:", setSessionErr);
          }
        }
      }
      
      // If we still don't have a session, clear stored tokens
      if (process.env.NODE_ENV === 'development') {
        logger.log("Supabase has no session and stored tokens failed - clearing tokens");
      }
      this.clearTokens();
      return null;
      
    } catch (supabaseError) {
      // Supabase session check failed - don't attempt refresh
      if (process.env.NODE_ENV === 'development') {
        logger.log("Supabase session check failed - not attempting refresh");
      }
      this.clearTokens();
      return null;
    }
    
    // REMOVED: Fallback to backend endpoint with stored tokens
    // This was causing 400 errors with stale tokens when Supabase has no session
  }

  private async retryRequest(originalRequest: RequestConfig, newToken: string) {
    if (!originalRequest.url) {
      throw new Error("No URL provided for retry request");
    }

    const retryConfig: RequestConfig = {
      ...originalRequest,
      headers: {
        ...originalRequest.headers,
        Authorization: `Bearer ${newToken}`,
      },
    };

    return fetch(originalRequest.url, retryConfig as any);
  }

  getAccessToken(): string | null {
    return secureStorage.getAccessToken();
  }

  getRefreshToken(): string | null {
    return secureStorage.getRefreshToken();
  }

  saveTokens(accessToken: string, refreshToken: string) {
    secureStorage.saveTokens(accessToken, refreshToken);
  }

  clearTokens() {
    secureStorage.clearTokens();
    // Stop token monitoring when tokens are cleared
    tokenMonitor.stopMonitoring();
      // Debug logging removed for cleaner console
  }

  redirectToLogin() {
    // Use our comprehensive logout utility for automatic logouts
    LogoutManager.silentLogout();
  }
}

export const authInterceptor = new AuthInterceptor();
